/**
 * ARTWORK STUDIO — CONFIG (pure; no React, no network).
 *
 * THE BACKEND OWNS THE OPTIONS. `GET /api/artwork/config` (sr-portal-backend
 * `src/artwork/renderService.js#artworkConfig`) returns the engine's real
 * option lists, and `mergeConfig()` folds that answer over the local defaults
 * below — so a new BEAST, Setting & Output preset, scale, view or script step
 * appears in this UI **with no frontend change**.
 *
 * The defaults here are NOT a second source of truth. They exist for exactly
 * two reasons: the studio stays usable when /config is unreachable (flagged in
 * the UI as fallback, never silently), and they mirror the shipped config so a
 * drift is visible rather than invented.
 *
 * WHAT /config DOES NOT SHIP YET (and is therefore local): products, ring
 * styles beyond the ones with a beast, metal tones/finishes, stone
 * shapes/settings/packages, girdle rows, action-bezel modes. Those live in
 * `LOCAL_OPTIONS` and are marked `fromConfig: false` in the UI. The moment the
 * backend adds them to `artworkConfig()`, `mergeConfig()` picks them up — the
 * merge already reads every one of these keys.
 *
 * Master Spec: Section 1 (Product×Style + Mode), Section 4 (12-step flow +
 * Ring Scale), Section 7 (feature library), Section 8A (Layer 0 presets).
 */

import { STONE_IDS, STONE_LABELS } from '../../../lib/stoneVocabulary.js'
import { PRODUCTS, STYLES, cellFor } from './productModel.js'

/* ------------------------------------------------------------------ *
 * Defaults that MIRROR the shipped backend config (prompt-config.js).
 * ------------------------------------------------------------------ */

export const FALLBACK_CONFIG = {
  settingPresets: [
    { id: 'studio-black', label: 'Studio Black', isDefault: true, slots: [] },
    { id: 'presentation-background', label: 'Presentation Background', isDefault: false, slots: ['BACKGROUND'] },
    { id: 'on-hand', label: 'On-Hand / Worn', isDefault: false, slots: [] },
    { id: 'white-seamless', label: 'White Seamless', isDefault: false, slots: [] },
    { id: 'contextual-venue', label: 'Contextual Venue', isDefault: false, slots: ['VENUE'] },
  ],
  defaultSettingPreset: 'studio-black',
  modes: [
    { id: 'render', label: 'Render (production-accurate)' },
    { id: 'generate', label: 'Generate (ideation)' },
  ],
  defaultMode: 'render',
  views: [
    { id: 'top', label: 'top / bezel face' },
    { id: 'left-shank', label: 'left shank' },
    { id: 'right-shank', label: 'right shank' },
    { id: 'palm', label: 'palm / interior' },
    { id: 'exploded', label: 'exploded / action' },
  ],
  defaultViews: ['top', 'left-shank', 'right-shank', 'palm', 'exploded'],
  scales: ['Small', 'Medium', 'Large', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'],
  defaultScale: 'Large',
  complexity: [1, 2, 3, 4, 5],
  defaultComplexity: 3,
  styles: [{ style: 'championship', beastId: 'championship-v1', beastLabel: 'Championship Beast Prompt v1' }],
  scriptSteps: [
    { key: 'logoC', label: 'Bezel logo (C)' },
    { key: 'bezel', label: 'Bezel & top design' },
    { key: 'girdle', label: 'Girdle' },
    { key: 'title', label: 'Title + variable attributes' },
    { key: 'shank1', label: 'Side shank 1' },
    { key: 'shank2', label: 'Side shank 2' },
    { key: 'palm', label: 'Palm side (interior text)' },
    { key: 'core', label: 'Core / interior engraving' },
    { key: 'stones', label: 'Feature gemstones' },
    { key: 'metal', label: 'Metal' },
    { key: 'complexity', label: 'Complexity' },
    { key: 'background', label: 'Background / presentation' },
  ],
  statuses: ['draft', 'proof', 'submitted', 'production'],
  renderReady: false,
  hostingReady: false,
}

/* ------------------------------------------------------------------ *
 * Options the engine accepts but /config does not enumerate yet.
 * Spec-derived (Section 1, Section 4, Section 7). Every one of these keys
 * is read by mergeConfig(), so shipping them from the backend overrides
 * this list without a frontend change.
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * SR TERMINOLOGY — Brian's terms, everywhere, no exceptions.
 *
 * THE SIX METALS SR ACTUALLY SELLS (Brian, 2026-08-10). Two house Vibrantium
 * compositions and four solid karat golds:
 *
 *     Vibrantium · Premium Vibrantium (.925 Silver) ·
 *     10K White Gold · 10K Yellow Gold · 14K White Gold · 14K Yellow Gold
 *
 * plus the **Vibrantium ID** color dropper (a custom color for a Vibrantium
 * piece) and the finish. The karat golds are REAL PRODUCTS and stay — an
 * earlier pass in this same release wrongly deleted them on the reading that
 * SR was Vibrantium-only. What IS retired is the pre-rebuild jeweler wording
 * with no product behind it: "Ultrium / Silver Alloy" (now **Vibrantium**),
 * "Sterling Silver" (now **Premium Vibrantium (.925 Silver)**), "Championship
 * Gold (plated)" (dropped outright — SR does not sell a plated metal), plus
 * "Prong", "Flush / gypsy", "Satin / Matte" and "No stone".
 *
 * THE ID IS THE LABEL HERE, deliberately. `metalTones` is the one list whose
 * value lands verbatim in a rendered prompt (`promptAssembler.js` puts it in
 * the METAL slot), and "10K White Gold" is the name of the thing — there is no
 * shorter true form of it. A slug would only have to be expanded back into
 * this exact string somewhere else. See the backend note on `metalText()` in
 * CLAUDE.md: the engine currently APPENDS the word "Vibrantium" to whatever it
 * receives, which was right when every metal was a Vibrantium tone and is
 * wrong now — that is a backend fix, and this file must not compensate for it
 * by shipping a mangled name.
 *
 * `/config` is the authority on WHICH options exist, and the backend is being
 * updated to serve these six labels as the source of truth. This layer only
 * decides how they READ — and it rewrites any terse key or legacy label the
 * backend still ships into Brian's wording, so the UI is correct before the
 * engine config is updated.
 * ------------------------------------------------------------------ */

/**
 * The metal list, in Brian's order. `id` is the exact label because that is
 * what the engine renders (see above).
 */
export const SR_METALS = [
  { id: 'Vibrantium', label: 'Vibrantium', vibrantium: true },
  { id: 'Premium Vibrantium (.925 Silver)', label: 'Premium Vibrantium (.925 Silver)', vibrantium: true },
  { id: '10K White Gold', label: '10K White Gold', vibrantium: false },
  { id: '10K Yellow Gold', label: '10K Yellow Gold', vibrantium: false },
  { id: '14K White Gold', label: '14K White Gold', vibrantium: false },
  { id: '14K Yellow Gold', label: '14K Yellow Gold', vibrantium: false },
]

/** The rainbow dropper beside the metal list. */
export const VIBRANTIUM_ID_LABEL = 'Vibrantium ID — custom Vibrantium color'

/**
 * The dropper sets a custom **Vibrantium** color, so it only means anything on
 * a Vibrantium composition. A custom hex on 14K Yellow Gold is not a product —
 * solid gold is the color it is — so the control is offered only where it is
 * real, rather than quietly posting a color the factory cannot make.
 */
export function allowsVibrantiumId(metalId) {
  const m = SR_METALS.find((x) => x.id === metalId)
  return m ? !!m.vibrantium : false
}

/** id → SR display term, per option list. */
export const SR_LABELS = {
  metalTones: Object.fromEntries(SR_METALS.map((m) => [m.id, m.label])),
  // Brian's wording is "polished / antiqued". The IDS stay the engine
  // config's own (`mirror-polish` / `antiqued`) — /config is the authority on
  // what gets posted, and renaming a value the backend already accepts would
  // buy nothing but a migration.
  metalFinishes: {
    'mirror-polish': 'Polished',
    antiqued: 'Antiqued',
  },
  /* ⚠️ THE STONE AXES ARE NOT DECLARED HERE ANY MORE. They live once in
   * `lib/stoneVocabulary.js`, shared with the Composer — this file and
   * `engine/stones.js` used to carry two different models of what a stone is
   * (shape + package here, gem type + color there), so the same ring
   * configured on two tabs of the same page produced records nobody could
   * compare. Settings were already identical by hand; the rest now cannot
   * drift, because there is only one list to edit. */
  stoneTypes: STONE_LABELS.stoneTypes,
  stoneSettings: STONE_LABELS.stoneSettings,
  stoneShapes: STONE_LABELS.stoneShapes,
  stonePackages: STONE_LABELS.stonePackages,
  girdleRows: { none: 'None', 1: '1 row', 2: '2 rows', 3: '3 rows' },
}

/**
 * LEGACY id/label → SR id. If the engine config (or a stored build) still
 * carries a pre-rebuild key, it RESOLVES onto the real product list rather than
 * rendering the old word.
 *
 * **`null` means RETIRED WITH NO EQUIVALENT, and it is dropped rather than
 * remapped.** "Championship Gold (plated)" is a plated finish and SR sells no
 * plated metal; silently resolving it onto 10K Yellow Gold would put a solid
 * precious metal on an order that never asked for one. Mis-stating a metal is
 * worse than showing one fewer option, so the chip does not render at all. The
 * karat golds map straight through, because they are the same real products
 * under their proper names.
 */
export const RETIRED = null

export const LEGACY_ALIASES = {
  metalTones: {
    // Renamed — same product, SR's name for it.
    'ultrium-silver': 'Vibrantium', 'ultrium / silver alloy': 'Vibrantium', ultrium: 'Vibrantium',
    'sterling-silver': 'Premium Vibrantium (.925 Silver)', 'sterling silver': 'Premium Vibrantium (.925 Silver)',
    sterling: 'Premium Vibrantium (.925 Silver)', '.925 silver': 'Premium Vibrantium (.925 Silver)',
    // Same products, canonical spelling of the id.
    'white-gold-10k': '10K White Gold', '10k white gold': '10K White Gold',
    'yellow-gold-10k': '10K Yellow Gold', '10k yellow gold': '10K Yellow Gold',
    'white-gold-14k': '14K White Gold', '14k white gold': '14K White Gold',
    'yellow-gold-14k': '14K Yellow Gold', '14k yellow gold': '14K Yellow Gold',
    vibrantium: 'Vibrantium',
    // Retired with nothing true to map onto.
    'championship-gold': RETIRED, 'championship gold (plated)': RETIRED,
    'rose-gold-10k': RETIRED, '10k rose gold': RETIRED, 'rose gold': RETIRED,
  },
  metalFinishes: {
    polished: 'mirror-polish', 'mirror polish': 'mirror-polish',
    antique: 'antiqued', antiqued: 'antiqued', 'satin': 'antiqued', 'satin / matte': 'antiqued',
  },
  stoneSettings: {
    prong: 'channel', 'bezel set': 'channel', bezel: 'channel',
    pave: 'pavé', flush: 'pavé', 'flush / gypsy': 'pavé',
  },
}

/**
 * Wording that must never reach the screen. This is the retired vocabulary
 * from the pre-rebuild Studio; `auditTerms()` runs these over every label the
 * Studio would render, so "the legacy terms are gone" is a TESTED property
 * rather than a claim — including against a `/config` that still ships them.
 */
export const LEGACY_PATTERNS = [
  // NOTE: 10K / 14K is deliberately NOT here. The karat golds are real SR
  // products — an earlier pass in this release banned them by mistake.
  /sterling/i,
  /ultrium/i,
  /silver alloy/i,
  /\bplated\b/i,
  /championship gold/i,
  /\bprong\b/i,
  /\bgypsy\b/i,
  /\bflush\b/i,
  /satin\s*\/?\s*matte/i,
  /\bbezel[- ]set\b/i,
  /\bno stone\b/i,
]

/** Does this text use retired wording? */
export function hasLegacyTerm(text) {
  const t = String(text == null ? '' : text)
  return LEGACY_PATTERNS.some((re) => re.test(t))
}

const titleize = (id) =>
  String(id).replace(/[-_]/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())

/**
 * One raw option (a string, or `{id,label}` from /config) → the SR option.
 * Resolution order, most authoritative first:
 *   1. a legacy alias remaps the id onto the real product list — and an alias
 *      of `RETIRED` (null) returns **null**, meaning "do not render this at
 *      all", which is how a discontinued metal disappears instead of being
 *      quietly reassigned to a different one
 *   2. SR_LABELS gives Brian's exact wording
 *   3. the config's own label, but ONLY if it is not retired wording
 *   4. a titleized id
 *
 * @returns {{id: string, label: string}|null} null when the option is retired.
 */
export function srOption(kind, raw) {
  const rawId = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : String(raw?.id ?? raw?.value ?? '')
  const rawLabel = (raw && typeof raw === 'object' && (raw.label || raw.name)) || ''
  const aliases = LEGACY_ALIASES[kind] || {}

  // `null` is a MEANINGFUL alias value, so membership is tested with `in` —
  // a `||` chain would read null as "no alias" and let the retired option
  // straight through, which is the exact bug this guards.
  let id = rawId
  for (const key of [rawId, rawId.toLowerCase(), String(rawLabel).toLowerCase()]) {
    if (key && key in aliases) {
      if (aliases[key] === RETIRED) return null
      id = aliases[key]
      break
    }
  }

  const srLabel = (SR_LABELS[kind] || {})[id]
  const label = srLabel || (rawLabel && !hasLegacyTerm(rawLabel) ? rawLabel : titleize(id))
  return { id, label }
}

/**
 * A config option list rendered in SR terms, de-duplicated by resolved id (so
 * two spellings of the same metal show ONE chip, not two) and with retired
 * options dropped entirely (`srOption` → null).
 */
export function optionsFor(config, kind) {
  const raw = (config && config[kind]) || []
  const out = []
  const seen = new Set()
  for (const r of raw) {
    const opt = srOption(kind, r)
    if (!opt || !opt.id || seen.has(opt.id)) continue
    seen.add(opt.id)
    out.push(opt)
  }
  return out
}

/**
 * Every label the Studio would render, with any that still trips the retired
 * vocabulary. `verify-studio.mjs` asserts this comes back empty — including
 * when fed a `/config` that ships the whole legacy metal list.
 */
export function auditTerms(config) {
  const kinds = ['metalTones', 'metalFinishes', 'stoneShapes', 'stoneSettings', 'stonePackages', 'girdleRows']
  const labels = []
  for (const kind of kinds) for (const o of optionsFor(config, kind)) labels.push({ kind, id: o.id, label: o.label })
  for (const p of config.settingPresets || []) labels.push({ kind: 'settingPresets', id: p.id, label: p.label })
  for (const v of config.views || []) labels.push({ kind: 'views', id: v.id, label: v.label })
  for (const s of config.scriptSteps || []) labels.push({ kind: 'scriptSteps', id: s.key, label: s.label })
  return { labels, offenders: labels.filter((l) => hasLegacyTerm(l.label)) }
}

export const LOCAL_OPTIONS = {
  /* Section 1 — the PRODUCT × STYLE matrix. Both axes live once, in
   * `productModel.js`, together with the per-product surfaces and the per-style
   * step sets that trim the flow. `available` is not a property of either axis
   * on its own — it is a property of the CELL (this product, in this style,
   * against the beast the engine actually has), so it is computed by
   * `productRows()` / `styleRows()` rather than stated here. */
  products: PRODUCTS.map((p) => ({ id: p.id, label: p.label, what: p.what, available: false })),
  styleCatalog: STYLES.map((s) => ({ id: s.id, label: s.label, what: s.what, confirmed: s.confirmed })),
  // Section 4 step 10 / Section 7 — THE SIX SR METALS (see SR_METALS): two
  // Vibrantium compositions and the four solid karat golds. The rainbow
  // dropper beside them sets a custom Vibrantium color and is offered only on
  // a Vibrantium composition (`allowsVibrantiumId`). The backend is being
  // updated to serve exactly these six labels from /config as the source of
  // truth; until it does, this list is what the studio shows.
  metalTones: SR_METALS,
  metalFinishes: ['mirror-polish', 'antiqued'],
  // Section 4 step 9 / Section 7 — stones.
  /* Section 4 step 9 / Section 7 — stones, from the ONE shared vocabulary.
   *
   * `stoneTypes` is deliberately ABSENT from the Builder's rendered steps: the
   * render contract's `components.stones` carries shapes / setting / package
   * and has no stone-material slot, so a type chip here would post a field the
   * engine drops and leave the operator believing they had specified a ruby.
   * It is a named item in docs/BACKEND-HANDOVER.md §2; the day /config serves
   * `stoneTypes`, `optionsFor()` picks it up with no change here. */
  stoneShapes: STONE_IDS.stoneShapes,
  stoneSettings: STONE_IDS.stoneSettings,
  stonePackages: STONE_IDS.stonePackages,
  // Section 4 step 3 — girdle rows.
  girdleRows: ['none', '1', '2', '3'],
  // Section 7 — the innovation lever.
  actionBezelModes: [
    { id: 'static', label: 'Static' },
    { id: 'opens', label: 'Opens / reveals' },
  ],
}

/* ------------------------------------------------------------------ *
 * Merge
 * ------------------------------------------------------------------ */

const list = (v) => (Array.isArray(v) && v.length ? v : null)

/**
 * ⚠️ THE BACKEND SHIPS THE METALS AND THIS FILE WAS NOT READING THEM.
 *
 * `renderService.js#artworkConfig()` returns the material line under `metals`
 * and `finishes` (verified against the shipped source, 2026-09-09):
 *
 *     metals: METALS.map((m) => ({ id, label, family, allowsVibrantiumId })),
 *     finishes: FINISHES.map((f) => ({ id, label })),
 *
 * `mergeConfig` only ever looked for `metalTones` / `metalFinishes`, so the
 * engine's own answer was fetched, ignored, and the local list rendered in its
 * place. Nothing looked wrong — the two lists agree today — which is exactly
 * the problem: a seventh metal added backend-side would have been invisible
 * here, and CLAUDE.md's promise that "`mergeConfig()` will fold them in with no
 * frontend change" was not true.
 *
 * Aliasing the wire name to the internal key is the whole fix. `optionsFor()`
 * then rewrites anything that arrives in retired wording, so the backend
 * winning does not mean the screen can regress (§12).
 */
export const CONFIG_KEY_ALIASES = {
  metals: 'metalTones',
  finishes: 'metalFinishes',
}

/**
 * Fold the live `/api/artwork/config` answer over the local defaults.
 *
 * @param {object|null} live  the API body (`{ ok, ...artworkConfig() }`)
 * @returns {object} config + `{ source: 'api'|'fallback' }`
 */
export function mergeConfig(live) {
  const c = live && typeof live === 'object' ? live : null
  const base = { ...FALLBACK_CONFIG, ...LOCAL_OPTIONS }
  if (!c) return { ...base, source: 'fallback' }

  const merged = { ...base, source: 'api' }
  // Every key the backend may ship — including the LOCAL_OPTIONS keys, so the
  // day artworkConfig() grows a `metalTones` it wins here with no code change.
  for (const key of [
    ...Object.keys(FALLBACK_CONFIG),
    ...Object.keys(LOCAL_OPTIONS),
  ]) {
    const v = c[key]
    if (v === undefined || v === null) continue
    if (Array.isArray(v)) { if (list(v)) merged[key] = v; continue }
    merged[key] = v
  }
  // The lists the backend ships under a different name than the one this file
  // reads them by. Aliased LAST so an explicit `metalTones` from a future
  // config still wins over `metals`.
  for (const [wire, key] of Object.entries(CONFIG_KEY_ALIASES)) {
    if (c[key] !== undefined && c[key] !== null && list(c[key])) continue
    if (list(c[wire])) merged[key] = c[wire]
  }
  // Scalars the backend always answers, even when false.
  for (const key of ['renderReady', 'hostingReady']) {
    if (typeof c[key] === 'boolean') merged[key] = c[key]
  }
  return merged
}

/** The style ids the engine will actually accept (i.e. that have a beast). */
export function availableStyles(config) {
  return (config.styles || []).map((s) => String(s.style || s.id || s)).filter(Boolean)
}

/**
 * The style selector's rows: the full spec catalog, each flagged with whether
 * the engine has a beast for it. A style with no beast is COMING — shown, never
 * selectable, never posted (the backend would 400 on it).
 */
export function styleRows(config, productId = 'ring') {
  const known = new Set((config.styleCatalog || []).map((s) => s.id))
  const rows = (config.styleCatalog || []).map((s) => {
    const cell = cellFor(productId, s.id, config)
    return {
      ...s,
      available: cell.available,
      blocked: cell.blocked,
      reason: cell.reason,
      beastLabel: (config.styles || []).find((x) => x.style === s.id)?.beastLabel || null,
    }
  })
  // A beast the catalog has never heard of still has to be selectable — the
  // backend is the authority on what it accepts, not this list.
  for (const s of config.styles || []) {
    if (!known.has(s.style)) {
      rows.push({ id: s.style, label: s.style, available: true, blocked: null, reason: '', beastLabel: s.beastLabel || null })
    }
  }
  return rows
}

/**
 * The product selector's rows, FOR THE STYLE CURRENTLY SELECTED.
 *
 * Availability is a property of the cell, not of the product: a pendant is a
 * real SR product and it is unbuildable today only because the one beast that
 * exists is written around a ring. Saying so — rather than flatly marking the
 * pendant "coming" — is the difference between a rep understanding what is
 * missing and concluding SR does not sell one.
 */
export function productRows(config, styleId = 'championship') {
  return (config.products || []).map((p) => {
    const cell = cellFor(p.id, styleId, config)
    return { ...p, available: cell.available, blocked: cell.blocked, reason: cell.reason }
  })
}

/** Which Layer 0 preset needs the sport/region/venue reveal (Section 8A). */
export function presetNeedsVenue(config, presetId) {
  const p = (config.settingPresets || []).find((x) => x.id === presetId)
  return !!(p && (p.slots || []).includes('VENUE'))
}

/** Which Layer 0 preset needs a background ref. */
export function presetNeedsBackground(config, presetId) {
  const p = (config.settingPresets || []).find((x) => x.id === presetId)
  return !!(p && (p.slots || []).includes('BACKGROUND'))
}
